import { makeAutoObservable } from "mobx";
import type { Team } from "@smartsuite/automation-engine-api";
import { mergeDirectoryEntries } from "./directory-merge";

export class TeamsStore {
  /**
   * Every team loaded in this session, deduplicated. For name lookup only --
   * pickers must use `forSolution` / `workspace`. See `MembersStore`.
   */
  list: Team[] = [];
  pageSize: number = 20;
  /**
   * Teams keyed by the solution they were loaded for. A solution restricted to
   * individual members exposes no teams at all.
   */
  bySolution: Record<string, Team[]> = {};
  /** Every team of the account, regardless of solution permissions. */
  workspace: Team[] = [];
  workspaceLoaded: boolean = false;

  constructor() {
    // see MembersStore for why these two stay unannotated
    makeAutoObservable(this, { forSolution: false, hasSolution: false });
  }

  setList(teams: Team[]): void {
    this.bySolution = {};
    this.workspace = [];
    this.workspaceLoaded = false;
    this.list = teams;
  }

  setForSolution(solutionId: string, teams: Team[]): void {
    this.bySolution = { ...this.bySolution, [solutionId]: teams };
    this.mergeList();
  }

  setWorkspace(teams: Team[]): void {
    this.workspace = teams;
    this.workspaceLoaded = true;
    this.mergeList();
  }

  /** Teams selectable on a field that writes into `solutionId`. */
  forSolution(solutionId: string): Team[] {
    return this.bySolution[solutionId] ?? [];
  }

  hasSolution(solutionId: string): boolean {
    return solutionId in this.bySolution;
  }

  reset(): void {
    this.bySolution = {};
    this.workspace = [];
    this.workspaceLoaded = false;
    this.list = [];
  }

  private mergeList(): void {
    this.list = mergeDirectoryEntries(
      [...Object.values(this.bySolution), this.workspace],
      (team) => team.teamId,
    );
  }
}
